import { useState, useEffect } from "react";
import CollapsibleSection from "./CollapsibleSection";
import ChakraCheckin from "./ChakraCheckin";
import ArchetypalThemes from "./ArchetypalThemes";
import SomaticBodyMap from "./SomaticBodyMap";
import IntentionChips from "./IntentionChips";
import NervousSystemTracking from "./NervousSystemTracking";
import PromptDisplay from "./PromptDisplay";
import { useSettings } from "../context/SettingsContext";

function EntryForm({
  onSubmit,
  isSubmitting,
  error,
  category,
  state,
  subcategory,
  intentionTags,
  onIntentionTagsChange,
  initialData = {},
  medicineType
}) {
  const { settings } = useSettings();

  const [title, setTitle] = useState(initialData.title || "");
  const [content, setContent] = useState(initialData.content || "");
  const [prompt, setPrompt] = useState(initialData.prompt || "");

  const toLocalDateTimeInput = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  const [sessionDate, setSessionDate] = useState(
    initialData.session_date
      ? toLocalDateTimeInput(initialData.session_date)
      : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
  );

  const [moodBefore, setMoodBefore] = useState(initialData.mood_before || "");
  const [moodAfter, setMoodAfter] = useState(initialData.mood_after || "");
  const [nervousSystemBefore, setNervousSystemBefore] = useState(initialData.nervous_system_before || "");
  const [nervousSystemAfter, setNervousSystemAfter] = useState(initialData.nervous_system_after || "");
  const [intention, setIntention] = useState(initialData.intention || "");
  const [intentionMet, setIntentionMet] = useState(initialData.intention_met || "");
  const [expectedEffects, setExpectedEffects] = useState(initialData.expected_effects || "");
  const [actualEffects, setActualEffects] = useState(initialData.actual_effects || "");

  const [chakraCheckins, setChakraCheckins] = useState(
    Array.isArray(initialData.chakra_checkins)
      ? initialData.chakra_checkins
      : initialData.chakra_checkins
        ? JSON.parse(initialData.chakra_checkins)
        : []
  );
  const [archetypalThemes, setArchetypalThemes] = useState(initialData.archetypal_themes || "");
  const [bodySensations, setBodySensations] = useState(
    Array.isArray(initialData.body_sensations)
      ? initialData.body_sensations
      : initialData.body_sensations
        ? JSON.parse(initialData.body_sensations)
        : []
  );

  useEffect(() => {
    setTitle(initialData.title || "");
    setContent(initialData.content || "");
    setPrompt(initialData.prompt || "");

    setSessionDate(
      initialData.session_date
        ? toLocalDateTimeInput(initialData.session_date)
        : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16)
    );

    setMoodBefore(initialData.mood_before || "");
    setMoodAfter(initialData.mood_after || "");
    setNervousSystemBefore(initialData.nervous_system_before || "");
    setNervousSystemAfter(initialData.nervous_system_after || "");
    setIntention(initialData.intention || "");
    setIntentionMet(initialData.intention_met || "");
    setExpectedEffects(initialData.expected_effects || "");
    setActualEffects(initialData.actual_effects || "");

    setChakraCheckins(
      Array.isArray(initialData.chakra_checkins)
        ? initialData.chakra_checkins
        : initialData.chakra_checkins
          ? JSON.parse(initialData.chakra_checkins)
          : []
    );

    setArchetypalThemes(initialData.archetypal_themes || "");

    setBodySensations(
      Array.isArray(initialData.body_sensations)
        ? initialData.body_sensations
        : initialData.body_sensations
          ? JSON.parse(initialData.body_sensations)
          : []
    );
  }, [initialData]);

  const isMushroom = medicineType === "mushroom";
  const isBefore = state === "Before";
  const isDuring = state === "During";
  const isAfter = state === "After";
  const isIntegration = state === "Integration";

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      title,
      content,
      session_date: sessionDate,
      mood_before: moodBefore || null,
      mood_after: moodAfter || null,
      nervous_system_before: nervousSystemBefore || null,
      nervous_system_after: nervousSystemAfter || null,
      intention,
      intention_tags: intentionTags,
      intention_met: intentionMet || null,
      expected_effects: expectedEffects,
      actual_effects: actualEffects,
      chakra_checkins: chakraCheckins,
      archetypal_themes: archetypalThemes,
      body_sensations: bodySensations,
      prompt,
    });
  };

  const journalPlaceholder = () => {
    if (isBefore) return isMushroom ? "Write your intentions and preparation notes..." : "Write your thoughts before the session...";
    if (isDuring) return isMushroom ? "Write what is moving through you..." : "Write what you're experiencing...";
    if (isAfter) return isMushroom ? "Reflect on your journey..." : "Reflect on your session...";
    if (isIntegration) return "What are you integrating? What wants to be carried forward?";
    return "Write your entry...";
  };

  const reflectionLabel = () => {
    if (isAfter && isMushroom) return "Journey Reflection";
    if (isAfter) return "Reflection";
    if (isIntegration) return "Integration Notes";
    return "Journal Entry";
  };

  const moodAfterLabel = () => {
  if (isDuring) return "Mood During";
  if (isAfter) return "Mood After";
  if (isIntegration) return "Mood After";
  return "Mood Before";
};

  return (
    <form className="EntryForm" onSubmit={handleSubmit}>
      <CollapsibleSection title="🕊️ Mood & Body" defaultOpen={false}>
        {(isBefore || isDuring || isAfter || isIntegration) && (
          <label>
            Mood Before
            <select
              value={moodBefore}
              onChange={(e) => setMoodBefore(e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="1">1 — Very low</option>
              <option value="2">2 — Low</option>
              <option value="3">3 — Neutral</option>
              <option value="4">4 — Good</option>
              <option value="5">5 — Excellent</option>
            </select>
          </label>
        )}

        {(isDuring || isAfter || isIntegration) && (
          <label>
            {moodAfterLabel()}
            <select
              value={moodAfter}
              onChange={(e) => setMoodAfter(e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="1">1 — Very low</option>
              <option value="2">2 — Low</option>
              <option value="3">3 — Neutral</option>
              <option value="4">4 — Good</option>
              <option value="5">5 — Excellent</option>
            </select>
          </label>
        )}

        {settings.show_mind_body && (
          <NervousSystemTracking
            valueBefore={nervousSystemBefore}
            valueAfter={nervousSystemAfter}
            onChangeBefore={setNervousSystemBefore}
            onChangeAfter={setNervousSystemAfter}
            isBefore={isBefore}
            isDuring={isDuring}
            isAfter={isAfter || isIntegration}
            isMushroom={isMushroom}
          />
        )}

        {settings.show_mind_body && (
          <div style={{ marginTop: "16px" }}>
            <p style={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px" }}>
              Chakra Check-in
            </p>
            <ChakraCheckin
              value={chakraCheckins}
              onChange={setChakraCheckins}
            />
          </div>
        )}

        {settings.show_mind_body && (
          <div style={{ marginTop: "16px" }}>
            <p style={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px" }}>
              Somatic Body Map
            </p>
            <SomaticBodyMap
              value={bodySensations}
              onChange={setBodySensations}
              medicine_type={medicineType}
            />
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="✨ Themes & Intention" defaultOpen={false}>
        {(isBefore || isDuring) && (
          <label>
            Intention
            <input
              type="text"
              value={intention}
              placeholder={
                isMushroom
                  ? isBefore
                    ? "What are you bringing into this journey?"
                    : "What is present for you right now?"
                  : isBefore
                    ? "What do you want from this session?"
                    : "What are you focusing on?"
              }
              onChange={(e) => setIntention(e.target.value)}
            />
          </label>
        )}

        {(subcategory || isMushroom) && (
          <div style={{ marginTop: "12px" }}>
            <IntentionChips
              value={intentionTags}
              onChange={onIntentionTagsChange}
              medicineType={medicineType}
            />
          </div>
        )}

        {(isDuring || isAfter || isIntegration) && (
          <>
            <label>
              Intention Met
              <select
                value={intentionMet}
                onChange={(e) => setIntentionMet(e.target.value)}
              >
                <option value="">-- Select --</option>
                <option value="1">1 — Not at all</option>
                <option value="2">2 — Slightly</option>
                <option value="3">3 — Somewhat</option>
                <option value="4">4 — Mostly</option>
                <option value="5">5 — Fully</option>
              </select>
            </label>

            <label>
              {isMushroom ? "What emerged?" : "Actual Effects"}
              <input
                type="text"
                value={actualEffects}
                placeholder={isMushroom ? "What arose that you want to capture?" : "What did you actually feel?"}
                onChange={(e) => setActualEffects(e.target.value)}
              />
            </label>
          </>
        )}

        {isBefore && (
          <label style={{ marginTop: "16px" }}>
            {isMushroom ? "What do you hope to access?" : "Expected Effects"}
            <input
              type="text"
              value={expectedEffects}
              placeholder={
                isMushroom
                  ? "What are you hoping this journey opens?"
                  : "What effects are you hoping for?"
              }
              onChange={(e) => setExpectedEffects(e.target.value)}
            />
          </label>
        )}

        {(isDuring || isAfter) && (
          <div style={{ marginTop: "16px" }}>
            <p style={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px" }}>
              {isDuring ? "Themes Emerging" : "Themes Present"}
            </p>
            <ArchetypalThemes
              value={archetypalThemes}
              onChange={setArchetypalThemes}
              isMushroom={isMushroom}
            />
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="📓 Journal" defaultOpen={true}>
        {category && state && subcategory && settings.show_prompt && (
          <div style={{ marginBottom: "12px" }}>
            <PromptDisplay
              category={category}
              state={state}
              subcategory={subcategory}
              prompt={prompt}
              onPromptChange={setPrompt}
              intentionTags={intentionTags}
              medicineType={medicineType}
            />
          </div>
        )}

        <label>
          Title
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <label>
          Date & Time
          <input
            type="datetime-local"
            value={sessionDate}
            max={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
            onChange={(e) => setSessionDate(e.target.value)}
          />
        </label>

        <label>
          {reflectionLabel()}
          <textarea
            rows={8}
            value={content}
            placeholder={journalPlaceholder()}
            onChange={(e) => setContent(e.target.value)}
          />
        </label>

        {error && <p className="error">{error}</p>}

        <button type="submit" disabled={isSubmitting} style={{ marginTop: "16px" }}>
          {isSubmitting ? "Saving..." : "Save Entry"}
        </button>
      </CollapsibleSection>
    </form>
  );
}

export default EntryForm;